import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
  addDays,
  adjustToTimeZone,
  firstOrUndefined, getDatabaseCodeSafely,
  getObjectId,
  MeterFlowVolumeEntity,
  MeterFlowVolumeSearchCriteria,
  meterFlowVolumesSelector,
  meterFlowVolumeTotalMatrixSelector, MeterFlowVolumeTotalSearchCriteria,
  startOfDay,
  subDays,
  TemporalMeterFlowVolumeTotal,
  TemporalMeterFlowVolumeTotalEntry, toTimeGroupSafely,
  TransportCategory,
  useSearchMeterFlowVolumesQuery,
  useSearchMeterFlowVolumeTotalsByDateQuery
} from "@ots-frontend/ots-data";
import {IconChevronDown, IconGauge} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {DataTableColumnGroup} from "mantine-datatable";
import {
  MeterFlowVolumeReviewSearchToolbar
} from "../../components/toolbar/MeterFlowVolumeReviewSearchToolbar/MeterFlowVolumeReviewSearchToolbar";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {getDateFormatterOptionsForTimeGroup} from "../../components/chart/TimeChart/TimeChart";
import {FormattedQuantity} from "../../components/text/FormattedQuantity/FormattedQuantity";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {DataTable} from "../../components/data-table/DataTable/DataTable";
import {
  MeterFlowVolumeReviewSearchFormValues
} from "../../components/form/MeterFlowVolumeReviewSearchForm/MeterFlowVolumeReviewSearchForm";
import {abs} from "mathjs";
import {
  MeterFlowVolumeDataTableModal,
  MeterFlowVolumeDataTableModalProps
} from "../../components/data-table/MeterFlowVolumeDataTable/MeterFlowVolumeDataTable";
import {MeterCell} from "../../components/data-table/cells/MeterCell/MeterCell";
import {useLocation, useNavigate} from "react-router-dom";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {selectMeterFlowVolumeReviewPageFilter} from "../../store/ui/meter-flow-volume-review-page/selectors";
import {setMeterFlowVolumeReviewPageFilter} from "../../store/ui/meter-flow-volume-review-page/actions";

type MeterFlowVolumeModalProps = Omit<MeterFlowVolumeDataTableModalProps, 'dataTable'> & {
  siteId: string
  fromDttm: number
  toDttm?: number,
  regionId?: string,
  category?: TransportCategory
}

const MeterFlowVolumeModal = (props: MeterFlowVolumeModalProps) => {
  const {siteId, fromDttm, toDttm, regionId, category, ...rest} = props

  const navigate = useNavigate()

  const selectMeterFlowVolumes = useMemo(() => {
    return meterFlowVolumesSelector();
  }, []);

  const searchArg: MeterFlowVolumeSearchCriteria = useMemo(() => {
    return {
      entityIds: [{id: siteId, type: 'SITE'}],
      fromDttm: fromDttm,
      toDttm: toDttm,
      regionIds: regionId ? [regionId] : undefined,
      categories: category ? [category.code] : undefined,
    }
  }, [siteId, fromDttm, toDttm, regionId, category])

  const {meterFlowVolumes, isFetching} = useSearchMeterFlowVolumesQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      meterFlowVolumes: selectMeterFlowVolumes(result, {regionId: regionId}),
      isFetching: result.isFetching,
      error: result.error,
    })
  });

  const onEdit = useCallback((record: MeterFlowVolumeEntity) => {
    navigate("/pipelines/meter-flows/form", {
      state: {
        id: record.id,
      }
    })
  }, [navigate])

  return (
    <MeterFlowVolumeDataTableModal
      {...rest}
      dataTable={{
        withBorder: true,
        records: meterFlowVolumes,
        recordsPerPageOptions: [5],
        fetching: isFetching,
        onEdit: onEdit,
      }}
    />
  )

}

export type MeterFlowVolumeReviewPageProps = {
  defaultSiteId?: string
  defaultFromDttm?: number
  defaultToDttm?: number
}

export const MeterFlowVolumeReviewPageRoute = () => {
  const {state} = useLocation();
  const siteId = state?.siteId;
  const fromDttm = state?.fromDttm
  const toDttm = state?.toDttm
  return <MeterFlowVolumeReviewPage defaultSiteId={siteId} defaultFromDttm={fromDttm} defaultToDttm={toDttm}/>;
};

export const MeterFlowVolumeReviewPage = (props: MeterFlowVolumeReviewPageProps) => {
  const {defaultSiteId, defaultFromDttm, defaultToDttm} = props;

  const dispatch = useDispatch()
  const {t} = useTranslation(["common"]);
  const {regionId, appCategory} = useApp();

  const [selected, setSelected] = useState<TemporalMeterFlowVolumeTotalEntry | undefined>(undefined)

  const criteria = useOtsUISelector(selectMeterFlowVolumeReviewPageFilter)

  useEffect(() => {
    dispatch(setMeterFlowVolumeReviewPageFilter({
      ...criteria,
      fromDttm: defaultFromDttm ?? criteria?.fromDttm,
      toDttm: defaultToDttm ?? criteria?.toDttm,
      entityIds: defaultSiteId ? [{
        id: defaultSiteId,
        type: 'SITE'
      }] : criteria?.entityIds,
    }));
  }, [])

  const searchArg: MeterFlowVolumeTotalSearchCriteria | typeof skipToken = useMemo(() => {
    if (criteria && criteria.fromDttm && criteria.entityIds) {
      return {
        ...criteria,
        fromDttm: adjustToTimeZone(criteria.fromDttm).getTime(),
        toDttm: adjustToTimeZone(criteria.toDttm).getTime(),
        categories: appCategory ? [appCategory.code] : undefined,
        regionIds: regionId ? [regionId] : undefined,
      }
    } else {
      return skipToken
    }
  }, [appCategory, regionId, criteria])

  const selectMeterFlowVolumeTotalMatrix = useMemo(() => {
    return meterFlowVolumeTotalMatrixSelector();
  }, []);

  const {matrix, isLoading, refetch} = useSearchMeterFlowVolumeTotalsByDateQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      matrix: selectMeterFlowVolumeTotalMatrix(result),
      isLoading: result.isLoading,
    })
  });

  const dateFormatter = useMemo(() => {
    const options = getDateFormatterOptionsForTimeGroup(toTimeGroupSafely(criteria?.timeGroup))
    return new Intl.DateTimeFormat(undefined, options)
  }, [criteria?.timeGroup])

  const onFilterChange = useCallback((values: MeterFlowVolumeReviewSearchFormValues) => {
    const siteId = getObjectId(values.site)

    dispatch(setMeterFlowVolumeReviewPageFilter({
      fromDttm: values.fromDate.getTime(),
      toDttm: values.toDate?.getTime() ?? addDays(values.fromDate, 1).getTime(),
      entityIds: siteId ? [{
        id: siteId,
        type: 'SITE'
      }] : undefined,
      timeGroup: getDatabaseCodeSafely(values.timeGroup),
    }));
  }, [dispatch]);

  const groups: DataTableColumnGroup<TemporalMeterFlowVolumeTotal>[] = useMemo(() => {
    const groups: DataTableColumnGroup<TemporalMeterFlowVolumeTotal>[] = []

    groups.push({
      id: '',
      columns: [{
        title: "Meter",
        accessor: "meterName",
        width: 400,
        render: ({meterId, meterName, meterType, flowMeterType,}) => (
          <MeterCell meterId={meterId} meterName={meterName} type={meterType} flowMeterType={flowMeterType}/>
        )
      }]
    })

    matrix?.dttms.forEach((dttm, index) => {
      groups.push({
        id: dttm.toString(),
        title: dateFormatter.format(dttm),
        columns: [{
          title: "Quantity",
          accessor: dttm + '_quantity',
          width: 200,
          render: ({totalsByDttm}) => {
            let quantity = 0
            let units: string | undefined = undefined

            if (totalsByDttm && dttm in totalsByDttm) {
              const entry = totalsByDttm[dttm]
              quantity = entry.quantity ?? 0
              units = entry.unitOfMeasure
            }

            return (
              <FormattedQuantity
                value={{
                  number: quantity,
                  units: units
                }}
              />
            )
          }
        }, {
          title: "Change",
          accessor: dttm + "_percent_change",
          width: 100,
          customCellAttributes: ({totalsByDttm}) => {
            if (totalsByDttm && dttm in totalsByDttm) {
              const percentChange = totalsByDttm[dttm].percentChange
              if (percentChange) {
                if (abs(percentChange) > 1) {
                  return {
                    "bgcolor": "orange",
                    "fw": 600,
                  }
                }
              }
            }

            return {}
          },
          render: ({totalsByDttm}) => {
            if (totalsByDttm && dttm in totalsByDttm) {
              const percentChange = totalsByDttm[dttm].percentChange
              if (percentChange) {
                return (
                  <FormattedQuantity
                    value={percentChange}
                    options={{
                      style: "percent",
                      signDisplay: "always",
                    }}
                  />
                )
              }
            }

            return "-"
          }
        }]
      })
    })

    return groups
  }, [matrix?.dttms, dateFormatter])

  const records: TemporalMeterFlowVolumeTotal[] = useMemo(() => {
    return matrix?.totals ?? []
  }, [matrix])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconGauge/>
                      <Title order={4}>
                        {t("common.page.meterflowvolume.review.title", "Meter Flow Volume Review")}
                      </Title>
                    </Group>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.action.plural", "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.action.refresh", "Refresh")}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <MeterFlowVolumeReviewSearchToolbar
                    defaultValues={{
                      fromDate: defaultFromDttm ? new Date(defaultFromDttm) : (criteria?.fromDttm ? new Date(criteria.fromDttm) : startOfDay(subDays(new Date(), 1))),
                      toDate: defaultToDttm ? new Date(defaultToDttm) : (criteria?.toDttm ? new Date(criteria.toDttm) : undefined),
                      site: firstOrUndefined(criteria?.entityIds)?.id,
                      timeGroup: criteria?.timeGroup,
                    }}
                    submitting={isLoading}
                    onSubmit={onFilterChange}/>
                </Card>
                <DataTable
                  withColumnBorders
                  records={records}
                  fetching={isLoading}
                  groups={groups}
                  onCellClick={({record, columnIndex}) => {
                    if (columnIndex > 0) {
                      const index = Math.floor((columnIndex - 1) / 2)
                      setSelected(Object.values(record.totalsByDttm)[index])
                    }
                  }}
                />
              </Stack>

              {(selected && criteria && (selected.fromDttm || criteria.fromDttm) && criteria.entityIds && criteria.entityIds.length > 0) &&
                <MeterFlowVolumeModal
                  size="xl"
                  opened={true}
                  onClose={() => setSelected(undefined)}
                  siteId={criteria.entityIds[0].id}
                  fromDttm={(selected.fromDttm ?? criteria.fromDttm)!}
                  toDttm={selected.toDttm ?? criteria.toDttm}
                  regionId={regionId}
                  category={appCategory}
                />
              }

            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
